import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import {
  PromoModal,
  IPromoModalProps,
  PurchaseTermsModal,
  PurchaseTermsModalContext,
} from '@cian/frontend-newbuilding-promo-modal-widget';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { selectOfferId } from 'shared/selectors';
import { selectMainPromo } from 'shared/selectors/newObject';
import { IExtraPrice } from 'shared/types/purchaseTerm/purchaseTermsV2';

import { preparePromo } from './utils/preparePromo';
import { PurchaseTerm, PurchaseTermsSection } from '../../components/PurchaseTermsSection';
import { selectExtraPrices } from '../../selectors/purchaseTerms/purchaseTermsV2';
import { useApplicationContext } from '../ApplicationContext';
import { PromoModalActionsContainer } from '../PromoModalActionsContainer';
import { trackBlockShow, trackBlockClick, trackPurchaseTermClick } from '../PurchaseTermsContainer/tracking';

type TComponentPromo = IPromoModalProps['promo'];

const DIALOG_EMPTY_CONFIG = {};

export const PurchaseTermsSectionContainer = () => {
  const context = useApplicationContext();

  const purchaseTermsV2 = useSelector(selectExtraPrices);
  const offerId = useSelector(selectOfferId);
  const mainPromo = useSelector(selectMainPromo);

  const extraPrices = purchaseTermsV2?.extraPrices;

  const [promo, setPromo] = React.useState<TComponentPromo | undefined>();
  const [openedPromoModal, setOpenedPromoModal] = React.useState(false);
  const [openedPurchaseTermsModal, setPurchaseTermsModal] = React.useState(false);

  const handleMoreButtonClick = React.useCallback(() => {
    trackBlockClick();
    setPurchaseTermsModal(true);
  }, []);
  const handlePurchaseTermsClose = React.useCallback(() => {
    setPurchaseTermsModal(false);
  }, []);

  const handleTermClick = React.useCallback((term: IExtraPrice) => {
    const preparedPromo = preparePromo(term.dialog);

    setPromo(preparedPromo);
    setOpenedPromoModal(true);

    trackPurchaseTermClick({
      type: term.type,
      source: 'Card',
    });
  }, []);

  const onClosePromoModal = React.useCallback(() => {
    setOpenedPromoModal(false);
    setPromo(undefined);
  }, []);

  if (!extraPrices || extraPrices.length === 0) {
    return null;
  }

  return (
    <>
      <ActionAfterViewObserver callback={trackBlockShow} triggerOnce>
        <PurchaseTermsSection
          onMoreButtonClick={handleMoreButtonClick}
          isModalOpen={openedPurchaseTermsModal || openedPromoModal}
        >
          {extraPrices.map((term, index) => (
            <PurchaseTerm
              key={index}
              title={term.name}
              logo={term.imageUrl}
              price={term.price}
              oldPrice={term.oldPrice}
              onClick={() => handleTermClick(term)}
            />
          ))}
        </PurchaseTermsSection>
      </ActionAfterViewObserver>
      {promo && (
        <PromoModal
          open={openedPromoModal}
          onOpenChange={onClosePromoModal}
          promo={promo}
          controls={<PromoModalActionsContainer modalType="purchase_term_modal" />}
          config={DIALOG_EMPTY_CONFIG}
        />
      )}
      <PurchaseTermsModalContext.Provider value={context}>
        <PurchaseTermsModal
          open={openedPurchaseTermsModal}
          onOpenChange={handlePurchaseTermsClose}
          offerId={offerId}
          controls={<PromoModalActionsContainer modalType="prices_modal" />}
          innerControls={<PromoModalActionsContainer modalType="purchase_term_modal" />}
          mainPromoId={mainPromo?.promoId}
          isDiscountPromo={mainPromo?.promoType === 'discount'}
        />
      </PurchaseTermsModalContext.Provider>
    </>
  );
};
